import { Billboard, Html, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { IconHandMove, IconMusic } from "@tabler/icons-react";
import * as THREE from "three";
import type { AvatarState } from "../../shared/types";
import { canPlayEmoteForAvatarState } from "../../shared/avatar-animations";
import type { RoomMotionFeed } from "../hooks/useRoomConnection";
import {
  buildTintedModel,
  type DrobbitAnimationMode,
  DESIRED_HEIGHT,
  DROBBIT_GLB,
  DROBBIT_WAVE_DURATION_MS,
  findDrobbitActionClips,
} from "../lib/drobbit-model";

const MODEL_YAW_OFFSET = -Math.PI / 2; // GLB faces +X; rotate to face +Z
const TURN_SPEED_IDLE = 14;
const TURN_SPEED_MOVING = 24;
const TELEPORT_DISTANCE = 2.5;
const MAX_FRAME_DELTA = 1 / 20;
const DEFAULT_INTERPOLATION_DELAY_MS = 130;
const SAMPLE_RETENTION_MS = 900;
const EXTRAPOLATION_LIMIT_MS = 420;
const REMOTE_MOVE_SPEED_UNITS_PER_SECOND = 1.8;
const SITTING_MODEL_OFFSET_Y = -0.12;
const LAYING_MODEL_OFFSET_Y = -0.2;
const LAYING_MODEL_OFFSET_Z = 0.32;
const LAYING_MODEL_PITCH = -Math.PI / 2;

function frameNowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

interface PositionSample {
  x: number;
  y: number;
  z: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  facing: number;
  t: number;
}

interface InterpolatedState {
  x: number;
  y: number;
  z: number;
  facing: number;
  segmentFacing: number | null;
  segmentAlpha: number;
  segmentIndex: number;
}

function shortestAngleDiff(from: number, to: number): number {
  let diff = to - from;
  while (diff > Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  return diff;
}

function lerpAngle(from: number, to: number, alpha: number): number {
  return from + shortestAngleDiff(from, to) * alpha;
}

function clampTowardTarget(current: number, target: number, predicted: number): number {
  const remaining = target - current;
  const after = target - predicted;
  if (remaining === 0) {
    return current;
  }
  if (remaining > 0 && after < 0) {
    return target;
  }
  if (remaining < 0 && after > 0) {
    return target;
  }
  return predicted;
}

function interpolateState(
  samples: PositionSample[],
  renderTime: number
): InterpolatedState {
  if (samples.length === 0) {
    return {
      x: 0,
      y: 0,
      z: 0,
      facing: 0,
      segmentFacing: null,
      segmentAlpha: 0,
      segmentIndex: 0,
    };
  }

  if (samples.length === 1 || renderTime <= samples[0].t) {
    const s = samples[0];
    return {
      x: s.x,
      y: s.y,
      z: s.z,
      facing: s.facing,
      segmentFacing: null,
      segmentAlpha: 0,
      segmentIndex: 0,
    };
  }

  const lastIndex = samples.length - 1;
  const newest = samples[lastIndex];
  if (renderTime > newest.t) {
    const previous = samples[lastIndex - 1];
    const dtMs = Math.min(renderTime - newest.t, EXTRAPOLATION_LIMIT_MS);
    const sampleDtSeconds = Math.max(0.001, (newest.t - previous.t) / 1000);

    let velocityX = (newest.x - previous.x) / sampleDtSeconds;
    let velocityY = (newest.y - previous.y) / sampleDtSeconds;
    let velocityZ = (newest.z - previous.z) / sampleDtSeconds;
    let facingFromVelocity: number | null = null;

    const planarVelocity = Math.hypot(velocityX, velocityZ);
    if (planarVelocity > 0.001) {
      facingFromVelocity = Math.atan2(velocityX, velocityZ);
    } else {
      const toTargetX = newest.targetX - newest.x;
      const toTargetZ = newest.targetZ - newest.z;
      const toTargetDist = Math.hypot(toTargetX, toTargetZ);
      if (toTargetDist > 0.001) {
        const normalizedX = toTargetX / toTargetDist;
        const normalizedZ = toTargetZ / toTargetDist;
        velocityX = normalizedX * REMOTE_MOVE_SPEED_UNITS_PER_SECOND;
        velocityZ = normalizedZ * REMOTE_MOVE_SPEED_UNITS_PER_SECOND;
        const toTargetY = newest.targetY - newest.y;
        velocityY =
          Math.abs(toTargetY) > 0.001
            ? Math.sign(toTargetY) *
              Math.min(Math.abs(toTargetY), REMOTE_MOVE_SPEED_UNITS_PER_SECOND)
            : 0;
        facingFromVelocity = Math.atan2(normalizedX, normalizedZ);
      }
    }

    const extrapolationSeconds = dtMs / 1000;
    const predictedX = newest.x + velocityX * extrapolationSeconds;
    const predictedY = newest.y + velocityY * extrapolationSeconds;
    const predictedZ = newest.z + velocityZ * extrapolationSeconds;

    return {
      x: clampTowardTarget(newest.x, newest.targetX, predictedX),
      y: clampTowardTarget(newest.y, newest.targetY, predictedY),
      z: clampTowardTarget(newest.z, newest.targetZ, predictedZ),
      facing: facingFromVelocity ?? newest.facing,
      segmentFacing: facingFromVelocity,
      segmentAlpha: 1,
      segmentIndex: Math.max(0, lastIndex - 1),
    };
  }

  let segmentIndex = samples.length - 2;
  for (let i = 0; i < samples.length - 1; i++) {
    if (renderTime <= samples[i + 1].t) {
      segmentIndex = i;
      break;
    }
  }

  const a = samples[segmentIndex];
  const b = samples[segmentIndex + 1];
  const span = Math.max(1, b.t - a.t);
  const alpha = THREE.MathUtils.clamp((renderTime - a.t) / span, 0, 1);

  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const segmentFacing =
    dx * dx + dz * dz > 0.000001 ? Math.atan2(dx, dz) : null;

  return {
    x: THREE.MathUtils.lerp(a.x, b.x, alpha),
    y: THREE.MathUtils.lerp(a.y, b.y, alpha),
    z: THREE.MathUtils.lerp(a.z, b.z, alpha),
    facing: segmentFacing ?? lerpAngle(a.facing, b.facing, alpha),
    segmentFacing,
    segmentAlpha: alpha,
    segmentIndex,
  };
}

const BUBBLE_FONT = "'Space Grotesk', sans-serif";
const BUBBLE_FONT_REGULAR_STR = `500 28px ${BUBBLE_FONT}`;
const BUBBLE_FONT_BOLD_STR = `700 28px ${BUBBLE_FONT}`;
const BUBBLE_MAX_W = 512;
const BUBBLE_LINE_H = 32;
const BUBBLE_PAD_X = 40; // horizontal padding inside bubble body
const BUBBLE_PAD_Y = 14; // vertical padding inside bubble body
const BUBBLE_SHADOW_MARGIN = 24; // extra canvas space for shadow
const BUBBLE_TAIL_H = 18;
const BUBBLE_R = 32;
const MAX_BUBBLE_STACK = 4;
const BUBBLE_STACK_GAP = 0.03;

export interface BubbleResult {
  texture: THREE.CanvasTexture;
  /** World-space width of the billboard plane */
  planeW: number;
  /** World-space height of the billboard plane */
  planeH: number;
  /** Visible bubble body width (without transparent canvas padding). */
  contentPlaneW: number;
  /** Visible bubble body height (without transparent canvas padding). */
  contentPlaneH: number;
}

interface BubbleToken {
  text: string;
  bold: boolean;
}

interface BubbleLineToken extends BubbleToken {
  width: number;
}

interface BubbleLine {
  tokens: BubbleLineToken[];
  width: number;
}

function setBubbleFont(ctx: CanvasRenderingContext2D, bold: boolean): void {
  ctx.font = bold ? BUBBLE_FONT_BOLD_STR : BUBBLE_FONT_REGULAR_STR;
}

function measureBubbleText(
  ctx: CanvasRenderingContext2D,
  text: string,
  bold: boolean
): number {
  setBubbleFont(ctx, bold);
  return ctx.measureText(text).width;
}

function sumBubbleLineWidth(line: BubbleLine): number {
  let width = 0;
  for (const token of line.tokens) {
    width += token.width;
  }
  return width;
}

function trimBubbleLineTrailingSpaces(line: BubbleLine): void {
  while (
    line.tokens.length > 0 &&
    /^\s+$/.test(line.tokens[line.tokens.length - 1].text)
  ) {
    line.tokens.pop();
  }
  line.width = sumBubbleLineWidth(line);
}

function fitBubbleTokenPrefix(
  ctx: CanvasRenderingContext2D,
  text: string,
  bold: boolean,
  maxWidth: number
): string {
  let low = 1;
  let high = text.length;
  let best = 0;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const probe = text.slice(0, mid);
    const width = measureBubbleText(ctx, probe, bold);
    if (width <= maxWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return best > 0 ? text.slice(0, best) : "";
}

function wrapBubbleTokens(
  ctx: CanvasRenderingContext2D,
  tokens: BubbleToken[],
  maxWidth: number,
  maxLines: number
): { lines: BubbleLine[]; truncated: boolean } {
  const lines: BubbleLine[] = [{ tokens: [], width: 0 }];
  let lineIndex = 0;
  let truncated = false;

  const pushNewLine = (): boolean => {
    if (lineIndex + 1 >= maxLines) {
      truncated = true;
      return false;
    }
    lineIndex += 1;
    lines.push({ tokens: [], width: 0 });
    return true;
  };

  for (const token of tokens) {
    let remaining = token.text;
    while (remaining.length > 0) {
      const line = lines[lineIndex];
      const spaceOnly = /^\s+$/.test(remaining);
      if (spaceOnly && line.tokens.length === 0) {
        break;
      }

      const availableWidth = Math.max(0, maxWidth - line.width);
      const fullWidth = measureBubbleText(ctx, remaining, token.bold);

      if (fullWidth <= availableWidth) {
        line.tokens.push({ text: remaining, bold: token.bold, width: fullWidth });
        line.width += fullWidth;
        remaining = "";
        continue;
      }

      if (line.tokens.length > 0) {
        if (!pushNewLine()) {
          remaining = "";
        }
        continue;
      }

      const chunk = fitBubbleTokenPrefix(ctx, remaining, token.bold, maxWidth);
      if (!chunk) {
        truncated = true;
        remaining = "";
        break;
      }
      const chunkWidth = measureBubbleText(ctx, chunk, token.bold);
      line.tokens.push({ text: chunk, bold: token.bold, width: chunkWidth });
      line.width += chunkWidth;
      remaining = remaining.slice(chunk.length);

      if (remaining.length > 0 && !pushNewLine()) {
        remaining = "";
      }
    }

    if (truncated) {
      break;
    }
  }

  for (const line of lines) {
    trimBubbleLineTrailingSpaces(line);
  }
  while (lines.length > 1 && lines[lines.length - 1].tokens.length === 0) {
    lines.pop();
  }

  return { lines, truncated };
}

function appendBubbleEllipsis(
  ctx: CanvasRenderingContext2D,
  line: BubbleLine,
  maxWidth: number
): void {
  const ellipsisText = "\u2026";
  const ellipsisWidth = measureBubbleText(ctx, ellipsisText, false);

  trimBubbleLineTrailingSpaces(line);
  while (line.tokens.length > 0 && line.width + ellipsisWidth > maxWidth) {
    const tail = line.tokens[line.tokens.length - 1];
    if (tail.text.length <= 1) {
      line.tokens.pop();
      line.width = sumBubbleLineWidth(line);
      continue;
    }
    tail.text = tail.text.slice(0, -1);
    tail.width = measureBubbleText(ctx, tail.text, tail.bold);
    trimBubbleLineTrailingSpaces(line);
  }

  line.tokens.push({ text: ellipsisText, bold: false, width: ellipsisWidth });
  line.width = sumBubbleLineWidth(line);
}

/** Dynamically sized speech bubble — shrinks for short text, grows up to max. */
export function createBubbleTexture(
  speakerName: string,
  text: string,
  withTail = true
): BubbleResult {
  // Measure text first to determine canvas size
  const measureCanvas = document.createElement("canvas");
  const mCtx = measureCanvas.getContext("2d")!;

  const maxTextW = BUBBLE_MAX_W - BUBBLE_PAD_X * 2 - BUBBLE_SHADOW_MARGIN * 2;
  const name = speakerName.trim() || "User";
  const messageText = text.trim();
  const tokens: BubbleToken[] = [{ text: `${name}:`, bold: true }];
  if (messageText) {
    tokens.push({ text: ` ${messageText}`, bold: false });
  }

  const wrapped = wrapBubbleTokens(mCtx, tokens, maxTextW, 3);
  const lines = wrapped.lines.length > 0 ? wrapped.lines : [{ tokens: [], width: 0 }];
  if (wrapped.truncated && lines.length > 0) {
    appendBubbleEllipsis(mCtx, lines[lines.length - 1], maxTextW);
  }

  // Compute actual needed width
  let widestLine = 0;
  for (const line of lines) {
    widestLine = Math.max(widestLine, line.width);
  }

  const bodyW = Math.min(
    BUBBLE_MAX_W - BUBBLE_SHADOW_MARGIN * 2,
    widestLine + BUBBLE_PAD_X * 2
  );
  const bodyH = lines.length * BUBBLE_LINE_H + BUBBLE_PAD_Y * 2;

  const canvasW = Math.ceil(bodyW + BUBBLE_SHADOW_MARGIN * 2);
  const canvasH = Math.ceil(
    bodyH +
      BUBBLE_SHADOW_MARGIN +
      (withTail ? BUBBLE_TAIL_H : 0) +
      BUBBLE_SHADOW_MARGIN
  );

  const canvas = document.createElement("canvas");
  canvas.width = canvasW;
  canvas.height = canvasH;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvasW, canvasH);

  const bodyX = BUBBLE_SHADOW_MARGIN;
  const bodyY = BUBBLE_SHADOW_MARGIN;

  // Drop shadow
  ctx.save();
  ctx.shadowColor = "rgba(0, 0, 0, 0.22)";
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 4;

  ctx.fillStyle = "#fafaf8";
  ctx.beginPath();
  ctx.roundRect(bodyX, bodyY, bodyW, bodyH, BUBBLE_R);
  ctx.fill();
  ctx.restore();

  // Subtle border
  ctx.strokeStyle = "rgba(0, 0, 0, 0.07)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.roundRect(bodyX, bodyY, bodyW, bodyH, BUBBLE_R);
  ctx.stroke();

  if (withTail) {
    // Small tail for the newest bubble only
    const tailCx = bodyX + bodyW * 0.44;
    const tailW = 14;
    const bodyBottom = bodyY + bodyH;
    ctx.fillStyle = "#fafaf8";
    ctx.beginPath();
    ctx.moveTo(tailCx - tailW / 2, bodyBottom - 1);
    ctx.lineTo(tailCx, bodyBottom + BUBBLE_TAIL_H);
    ctx.lineTo(tailCx + tailW / 2, bodyBottom - 1);
    ctx.closePath();
    ctx.fill();
  }

  // Render text
  ctx.fillStyle = "#2c2c2c";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  const totalTextH = lines.length * BUBBLE_LINE_H;
  const textStartY = bodyY + bodyH / 2 - totalTextH / 2 + BUBBLE_LINE_H / 2;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let cursorX = bodyX + (bodyW - line.width) / 2;
    const baselineY = textStartY + i * BUBBLE_LINE_H;
    for (const token of line.tokens) {
      setBubbleFont(ctx, token.bold);
      ctx.fillText(token.text, cursorX, baselineY);
      cursorX += token.width;
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;

  // Convert canvas pixels to world-space plane size
  // The max bubble (512px canvas) was displayed at 1.8 world units wide
  const scale = 1.8 / BUBBLE_MAX_W;
  return {
    texture: tex,
    planeW: canvasW * scale,
    planeH: canvasH * scale,
    contentPlaneW: bodyW * scale,
    contentPlaneH: bodyH * scale,
  };
}

interface Props {
  avatar: AvatarState;
  isSelf: boolean;
  isLaying?: boolean;
  sampleTimeMs?: number | null;
  motionFeedRef?: MutableRefObject<RoomMotionFeed>;
  onSelect?: (avatar: AvatarState) => void;
  showChatBubbles?: boolean;
  showEmoteWheel?: boolean;
  onToggleEmoteWheel?: () => void;
  onDanceEmote?: () => void;
  onWaveEmote?: () => void;
}

function DrobbitModel({
  color,
  mode,
}: {
  color: string;
  mode: DrobbitAnimationMode;
}) {
  const { scene, animations } = useGLTF(DROBBIT_GLB, true) as {
    scene: THREE.Group;
    animations: THREE.AnimationClip[];
  };
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const actionsRef = useRef<{
    walk: THREE.AnimationAction | null;
    dance: THREE.AnimationAction | null;
    wave: THREE.AnimationAction | null;
    sit: THREE.AnimationAction | null;
  }>({
    walk: null,
    dance: null,
    wave: null,
    sit: null,
  });
  const modeRef = useRef<DrobbitAnimationMode>(mode);
  const applyMode = (nextMode: DrobbitAnimationMode) => {
    const { walk, dance, wave, sit } = actionsRef.current;
    if (!walk && !dance && !wave && !sit) return;
    const prevMode = modeRef.current;

    if (nextMode === "walk") {
      if (walk && prevMode !== "walk") {
        walk.reset();
      }
      walk?.play();
      walk?.setEffectiveWeight(1);
      walk?.setEffectiveTimeScale(1);
      dance?.setEffectiveWeight(0);
      wave?.setEffectiveWeight(0);
      return;
    }

    if (nextMode === "dance") {
      if (dance && prevMode !== "dance") {
        dance.reset();
      }
      dance?.play();
      dance?.setEffectiveWeight(1);
      dance?.setEffectiveTimeScale(1);
      walk?.setEffectiveWeight(0);
      wave?.setEffectiveWeight(0);
      return;
    }

    if (nextMode === "wave") {
      if (wave && prevMode !== "wave") {
        wave.reset();
      }
      wave?.play();
      wave?.setEffectiveWeight(1);
      wave?.setEffectiveTimeScale(1);
      walk?.setEffectiveWeight(0);
      dance?.setEffectiveWeight(0);
      return;
    }

    if (nextMode === "sit") {
      if (sit && prevMode !== "sit") {
        sit.reset();
      }
      sit?.play();
      sit?.setEffectiveWeight(1);
      sit?.setEffectiveTimeScale(1);
      walk?.setEffectiveWeight(0);
      dance?.setEffectiveWeight(0);
      wave?.setEffectiveWeight(0);
      return;
    }

    walk?.setEffectiveWeight(0);
    dance?.setEffectiveWeight(0);
    wave?.setEffectiveWeight(0);
    sit?.setEffectiveWeight(0);
  };

  const model = useMemo(() => {
    const { group } = buildTintedModel(scene, color);
    return group;
  }, [scene, color]);

  useEffect(() => {
    const mixer = new THREE.AnimationMixer(model);
    mixerRef.current = mixer;
    const clips = findDrobbitActionClips(animations);
    const walk = clips.walk ? mixer.clipAction(clips.walk) : null;
    const dance = clips.dance ? mixer.clipAction(clips.dance) : null;
    const wave = clips.wave ? mixer.clipAction(clips.wave) : null;
    const sit = clips.sit ? mixer.clipAction(clips.sit) : null;
    if (!walk || !dance || !wave) {
      console.warn("[DrobbitAvatar] Missing expected clips", {
        expected: ["Walk", "Dance", "Wave"],
        available: animations.map((clip) => clip.name),
      });
    }
    actionsRef.current = { walk, dance, wave, sit };

    for (const action of [walk, dance, wave, sit]) {
      if (!action) continue;
      const isWave = action === wave;
      action.enabled = true;
      action.setLoop(isWave ? THREE.LoopOnce : THREE.LoopRepeat, Infinity);
      action.clampWhenFinished = isWave;
      action.setEffectiveWeight(0);
      action.setEffectiveTimeScale(1);
      action.play();
    }
    modeRef.current = "idle";
    applyMode(mode);

    return () => {
      const {
        walk: currentWalk,
        dance: currentDance,
        wave: currentWave,
        sit: currentSit,
      } = actionsRef.current;
      for (const action of [currentWalk, currentDance, currentWave, currentSit]) {
        action?.stop();
      }
      mixer.stopAllAction();
      mixer.uncacheRoot(model);
      mixerRef.current = null;
      actionsRef.current = { walk: null, dance: null, wave: null, sit: null };
    };
  }, [model, animations]);

  useEffect(() => {
    const previous = modeRef.current;
    if (previous === mode) return;
    applyMode(mode);
    modeRef.current = mode;
  }, [mode]);

  useFrame((_, delta) => {
    mixerRef.current?.update(delta);
  });

  return <primitive object={model} />;
}

function DrobbitAvatarInner({
  avatar,
  isSelf,
  isLaying = false,
  sampleTimeMs,
  motionFeedRef,
  onSelect,
  showChatBubbles = true,
  showEmoteWheel = false,
  onToggleEmoteWheel,
  onDanceEmote,
  onWaveEmote,
}: Props) {
  const groupRef = useRef<THREE.Group | null>(null);
  const modelWrapRef = useRef<THREE.Group | null>(null);
  const [messageStack, setMessageStack] = useState<
    Array<{ text: string; at: number }>
  >([]);
  const samplesRef = useRef<PositionSample[]>([
    {
      x: avatar.x,
      y: avatar.y,
      z: avatar.z,
      targetX: avatar.targetX,
      targetY: avatar.targetY,
      targetZ: avatar.targetZ,
      facing: avatar.facing ?? 0,
      t: frameNowMs(),
    },
  ]);
  const [isWalkingVisual, setIsWalkingVisual] = useState(false);
  const [isWavingVisual, setIsWavingVisual] = useState(false);
  const [emoteHoverLabel, setEmoteHoverLabel] = useState<string | null>(null);
  const isWalkingVisualRef = useRef(false);
  const isWavingVisualRef = useRef(false);
  const waveUntilRef = useRef(0);
  const lastWaveAtRef = useRef<number | null>(null);
  const walkHoldUntilRef = useRef(0);
  const lastVisualPosRef = useRef<{ x: number; z: number } | null>(null);
  const lastAppliedMotionSeqRef = useRef<number>(-1);

  const pushSample = (
    x: number,
    y: number,
    z: number,
    targetX: number,
    targetY: number,
    targetZ: number,
    facing: number,
    sampleT: number
  ): void => {
    const samples = samplesRef.current;
    const last = samples[samples.length - 1];
    const now = Math.max(sampleT, (last?.t ?? sampleT) + 0.1);
    if (!last) {
      samples.push({ x, y, z, targetX, targetY, targetZ, facing, t: now });
      return;
    }
    const moved =
      Math.abs(last.x - x) > 0.0001 ||
      Math.abs(last.y - y) > 0.0001 ||
      Math.abs(last.z - z) > 0.0001;
    const rotated = Math.abs(shortestAngleDiff(last.facing, facing)) > 0.0001;
    if (moved || rotated) {
      samples.push({ x, y, z, targetX, targetY, targetZ, facing, t: now });
      if (samples.length > 20) {
        samples.splice(0, samples.length - 20);
      }
    } else {
      last.targetX = targetX;
      last.targetY = targetY;
      last.targetZ = targetZ;
      last.t = now;
    }
  };

  useEffect(() => {
    const baseTime = sampleTimeMs ?? frameNowMs();
    const nextFacing = avatar.facing ?? 0;
    pushSample(
      avatar.x,
      avatar.y,
      avatar.z,
      avatar.targetX,
      avatar.targetY,
      avatar.targetZ,
      nextFacing,
      baseTime
    );
  }, [
    avatar.x,
    avatar.y,
    avatar.z,
    avatar.targetX,
    avatar.targetY,
    avatar.targetZ,
    avatar.facing,
  ]);

  useEffect(() => {
    const now = frameNowMs();
    const motion = motionFeedRef?.current.byId.get(avatar.id);
    const resetX = motion?.x ?? avatar.x;
    const resetY = motion?.y ?? avatar.y;
    const resetZ = motion?.z ?? avatar.z;
    const resetTargetX = motion?.targetX ?? avatar.targetX ?? resetX;
    const resetTargetY = motion?.targetY ?? avatar.targetY ?? resetY;
    const resetTargetZ = motion?.targetZ ?? avatar.targetZ ?? resetZ;
    const resetFacing = motion?.facing ?? (avatar.facing ?? 0);
    const interpolationDelayMs =
      motionFeedRef?.current.interpolationDelayMs ??
      DEFAULT_INTERPOLATION_DELAY_MS;
    samplesRef.current = [
      {
        x: resetX,
        y: resetY,
        z: resetZ,
        targetX: resetTargetX,
        targetY: resetTargetY,
        targetZ: resetTargetZ,
        facing: resetFacing,
        t: now - interpolationDelayMs,
      },
      {
        x: resetX,
        y: resetY,
        z: resetZ,
        targetX: resetTargetX,
        targetY: resetTargetY,
        targetZ: resetTargetZ,
        facing: resetFacing,
        t: now,
      },
    ];
    lastAppliedMotionSeqRef.current = motion?.updateSeq ?? -1;

    if (groupRef.current) {
      groupRef.current.position.set(resetX, resetY, resetZ);
    }
    if (modelWrapRef.current) {
      modelWrapRef.current.rotation.y = resetFacing + MODEL_YAW_OFFSET;
      modelWrapRef.current.rotation.x = 0;
      modelWrapRef.current.position.y = 0;
      modelWrapRef.current.position.z = 0;
    }
    walkHoldUntilRef.current = 0;
    lastVisualPosRef.current = { x: resetX, z: resetZ };
    isWalkingVisualRef.current = false;
    setIsWalkingVisual(false);
    const waveAt = avatar.waveAt;
    if (typeof waveAt === "number") {
      const elapsed = Math.max(0, Date.now() - waveAt);
      const remaining = Math.max(0, DROBBIT_WAVE_DURATION_MS - elapsed);
      waveUntilRef.current = now + remaining;
      const nextWaveVisual = remaining > 0;
      isWavingVisualRef.current = nextWaveVisual;
      setIsWavingVisual(nextWaveVisual);
      lastWaveAtRef.current = waveAt;
    } else {
      waveUntilRef.current = 0;
      isWavingVisualRef.current = false;
      setIsWavingVisual(false);
      lastWaveAtRef.current = null;
    }
  }, [avatar.id]);

  useEffect(() => {
    const nextWaveAt = avatar.waveAt;
    const previousWaveAt = lastWaveAtRef.current;
    if (nextWaveAt === previousWaveAt) {
      return;
    }
    lastWaveAtRef.current = typeof nextWaveAt === "number" ? nextWaveAt : null;
    const now = frameNowMs();
    if (typeof nextWaveAt !== "number") {
      waveUntilRef.current = 0;
      if (isWavingVisualRef.current) {
        isWavingVisualRef.current = false;
        setIsWavingVisual(false);
      }
      return;
    }
    // A new wave timestamp should always trigger a full local wave animation.
    waveUntilRef.current = now + DROBBIT_WAVE_DURATION_MS;
    const nextWaveVisual = true;
    if (nextWaveVisual !== isWavingVisualRef.current) {
      isWavingVisualRef.current = nextWaveVisual;
      setIsWavingVisual(nextWaveVisual);
    }
  }, [avatar.waveAt]);

  useEffect(() => {
    if (!showChatBubbles) {
      setMessageStack([]);
      return;
    }
    if (avatar.lastMessage && avatar.lastMessageAt) {
      setMessageStack([{ text: avatar.lastMessage, at: avatar.lastMessageAt }]);
    } else {
      setMessageStack([]);
    }
  }, [avatar.id, showChatBubbles]);

  useEffect(() => {
    if (!showChatBubbles) {
      return;
    }
    if (!avatar.lastMessage || !avatar.lastMessageAt) return;
    const nextText = avatar.lastMessage;
    const nextAt = avatar.lastMessageAt;
    setMessageStack((prev) => {
      const latest = prev[prev.length - 1];
      if (latest && latest.text === nextText && latest.at === nextAt) {
        return prev;
      }
      return [...prev, { text: nextText, at: nextAt }].slice(
        -MAX_BUBBLE_STACK
      );
    });
  }, [avatar.lastMessage, avatar.lastMessageAt, showChatBubbles]);

  useEffect(() => {
    if (!showEmoteWheel) {
      setEmoteHoverLabel(null);
    }
  }, [showEmoteWheel]);

  useFrame((_, delta) => {
    const motion = motionFeedRef?.current.byId.get(avatar.id);
    if (
      motion &&
      Number.isFinite(motion.updateSeq) &&
      motion.updateSeq !== lastAppliedMotionSeqRef.current
    ) {
      lastAppliedMotionSeqRef.current = motion.updateSeq;
      pushSample(
        motion.x,
        motion.y,
        motion.z,
        motion.targetX,
        motion.targetY,
        motion.targetZ,
        motion.facing,
        motion.sampleTimeMs
      );
    }

    const group = groupRef.current;
    if (!group) return;
    const stepDelta = Math.min(delta, MAX_FRAME_DELTA);
    const now = frameNowMs();
    const interpolationDelayMs =
      motionFeedRef?.current.interpolationDelayMs ??
      DEFAULT_INTERPOLATION_DELAY_MS;
    const renderTime = now - interpolationDelayMs;
    const samples = samplesRef.current;

    while (
      samples.length > 2 &&
      samples[1].t <= renderTime - SAMPLE_RETENTION_MS
    ) {
      samples.shift();
    }

    const state = interpolateState(samples, renderTime);
    const seatedNow = avatar.isSitting === true;
    const layingNow = seatedNow && isLaying;
    const renderX = seatedNow ? avatar.x : state.x;
    const renderY = seatedNow ? avatar.y : state.y;
    const renderZ = seatedNow ? avatar.z : state.z;

    const errX = renderX - group.position.x;
    const errY = renderY - group.position.y;
    const errZ = renderZ - group.position.z;
    if (
      errX * errX + errY * errY + errZ * errZ >
      TELEPORT_DISTANCE * TELEPORT_DISTANCE
    ) {
      group.position.set(renderX, renderY, renderZ);
    } else {
      group.position.set(renderX, renderY, renderZ);
    }

    if (modelWrapRef.current) {
      let targetFacing =
        seatedNow && Number.isFinite(avatar.facing)
          ? (avatar.facing ?? state.facing)
          : state.facing;

      // Start rotating slightly before a corner by blending into the next segment heading.
      if (
        !seatedNow &&
        state.segmentFacing != null &&
        state.segmentIndex + 2 < samples.length &&
        state.segmentAlpha > 0.65
      ) {
        const b = samples[state.segmentIndex + 1];
        const c = samples[state.segmentIndex + 2];
        const nextDx = c.x - b.x;
        const nextDz = c.z - b.z;
        if (nextDx * nextDx + nextDz * nextDz > 0.000001) {
          const nextFacing = Math.atan2(nextDx, nextDz);
          const blend = THREE.MathUtils.clamp(
            (state.segmentAlpha - 0.65) / 0.35,
            0,
            1
          );
          const smoothBlend = blend * blend * (3 - 2 * blend);
          targetFacing = lerpAngle(
            state.segmentFacing,
            nextFacing,
            smoothBlend
          );
        }
      }

      const targetRotY = targetFacing + MODEL_YAW_OFFSET;
      const currentRotY = modelWrapRef.current.rotation.y;
      const diff = shortestAngleDiff(currentRotY, targetRotY);
      const maxTurnStep =
        (!seatedNow && state.segmentFacing != null
          ? TURN_SPEED_MOVING
          : TURN_SPEED_IDLE) *
        stepDelta;
      modelWrapRef.current.rotation.y =
        currentRotY + THREE.MathUtils.clamp(diff, -maxTurnStep, maxTurnStep);

      const desiredModelOffsetY = seatedNow
        ? layingNow
          ? LAYING_MODEL_OFFSET_Y
          : SITTING_MODEL_OFFSET_Y
        : 0;
      const desiredModelOffsetZ = layingNow ? LAYING_MODEL_OFFSET_Z : 0;
      const desiredModelPitch = layingNow ? LAYING_MODEL_PITCH : 0;
      const poseLerpAlpha = 1 - Math.exp(-12 * stepDelta);
      const nextModelOffsetY = THREE.MathUtils.lerp(
        modelWrapRef.current.position.y,
        desiredModelOffsetY,
        poseLerpAlpha
      );
      const nextModelOffsetZ = THREE.MathUtils.lerp(
        modelWrapRef.current.position.z,
        desiredModelOffsetZ,
        poseLerpAlpha
      );
      const nextModelPitch = THREE.MathUtils.lerp(
        modelWrapRef.current.rotation.x,
        desiredModelPitch,
        poseLerpAlpha
      );
      modelWrapRef.current.position.y =
        Math.abs(nextModelOffsetY - desiredModelOffsetY) <= 0.0007
          ? desiredModelOffsetY
          : nextModelOffsetY;
      modelWrapRef.current.position.z =
        Math.abs(nextModelOffsetZ - desiredModelOffsetZ) <= 0.0007
          ? desiredModelOffsetZ
          : nextModelOffsetZ;
      modelWrapRef.current.rotation.x =
        Math.abs(nextModelPitch - desiredModelPitch) <= 0.0007
          ? desiredModelPitch
          : nextModelPitch;
    }

    const previous = lastVisualPosRef.current;
    const nowMs = frameNowMs();
    let movingByDelta = false;
    if (previous) {
      const planarDelta = Math.hypot(
        renderX - previous.x,
        renderZ - previous.z
      );
      movingByDelta = planarDelta > 0.0007;
    }
    lastVisualPosRef.current = { x: renderX, z: renderZ };

    const targetX = seatedNow ? avatar.targetX : motion?.targetX ?? avatar.targetX;
    const targetZ = seatedNow ? avatar.targetZ : motion?.targetZ ?? avatar.targetZ;
    const distanceToTarget = Math.hypot(
      targetX - renderX,
      targetZ - renderZ
    );
    const advancingOnPath = distanceToTarget > 0.03;

    if (movingByDelta || advancingOnPath) {
      walkHoldUntilRef.current = nowMs + 120;
    } else {
      // Drop out quickly when there is no actual movement/progress.
      walkHoldUntilRef.current = Math.min(walkHoldUntilRef.current, nowMs);
    }

    const shouldWalk =
      !avatar.isDancing &&
      !avatar.isSitting &&
      nowMs <= walkHoldUntilRef.current;
    if (shouldWalk !== isWalkingVisualRef.current) {
      isWalkingVisualRef.current = shouldWalk;
      setIsWalkingVisual(shouldWalk);
    }

    const shouldWave = nowMs <= waveUntilRef.current;
    if (shouldWave !== isWavingVisualRef.current) {
      isWavingVisualRef.current = shouldWave;
      setIsWavingVisual(shouldWave);
    }
  });

  const danceHovered = emoteHoverLabel === "dance";
  const waveHovered = emoteHoverLabel === "wave";
  const canDanceWhileCurrentState = canPlayEmoteForAvatarState(
    "dance",
    avatar.isSitting
  );
  const animationMode: DrobbitAnimationMode = isWavingVisual
    ? "wave"
    : avatar.isSitting
      ? "sit"
    : avatar.isDancing
      ? "dance"
      : isWalkingVisual
        ? "walk"
        : "idle";

  const BUBBLE_DURATION = 7000;
  const BUBBLE_FADE_MS = 1500;
  const stackedBubbles = useMemo(() => {
    if (!showChatBubbles || messageStack.length === 0) {
      return [];
    }
    const bubbles = messageStack.map((message, idx) => {
      const isNewest = idx === messageStack.length - 1;
      return {
        key: `${message.at}:${idx}`,
        at: message.at,
        bubble: createBubbleTexture(avatar.name, message.text, isNewest),
      };
    });

    // Start from newest at the bottom and stack older bubbles above it.
    const newestFirst = bubbles.reverse();
    let bottomY = DESIRED_HEIGHT + 0.15;
    return newestFirst.map((entry, idx) => {
      const centerY = bottomY + entry.bubble.planeH / 2;
      bottomY += entry.bubble.planeH + BUBBLE_STACK_GAP;
      return {
        ...entry,
        centerY,
        stackIndex: idx,
      };
    });
  }, [avatar.name, messageStack, showChatBubbles]);

  const bubbleMatRefs = useRef<Array<THREE.MeshBasicMaterial | null>>([]);

  useEffect(() => {
    return () => {
      for (const entry of stackedBubbles) {
        entry.bubble.texture.dispose();
      }
    };
  }, [stackedBubbles]);

  // Fade-out animation for stacked speech bubbles
  useFrame(() => {
    for (const entry of stackedBubbles) {
      const mat = bubbleMatRefs.current[entry.stackIndex];
      if (!mat) continue;
      const elapsed = Date.now() - entry.at;
      if (elapsed > BUBBLE_DURATION) {
        mat.opacity = 0;
      } else if (elapsed > BUBBLE_DURATION - BUBBLE_FADE_MS) {
        const fadeProgress =
          (elapsed - (BUBBLE_DURATION - BUBBLE_FADE_MS)) / BUBBLE_FADE_MS;
        mat.opacity = 1 - fadeProgress;
      } else {
        mat.opacity = 1;
      }
    }
  });

  return (
    <>
      <group ref={groupRef}>
        <group
          ref={modelWrapRef}
          onPointerOver={(e) => {
            e.stopPropagation();
            document.body.style.cursor = "pointer";
          }}
          onPointerOut={() => {
            document.body.style.cursor = "";
          }}
          onPointerDown={(e) => {
            // Block floor click-to-move when the click intersects an avatar.
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isSelf) {
              onToggleEmoteWheel?.();
              return;
            }
            onSelect?.(avatar);
          }}
        >
          <DrobbitModel
            color={avatar.color || "#000000"}
            mode={animationMode}
          />
        </group>

        {isSelf && showEmoteWheel && (
          <Html
            position={[0, DESIRED_HEIGHT + 0.46, 0]}
            center
            transform={false}
            style={{ pointerEvents: "none" }}
          >
            <div
              style={{
                width: 136,
                height: 136,
                position: "relative",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 10,
                  borderRadius: "50%",
                  border: "1px solid rgba(255,255,255,0.26)",
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(14,18,28,0.52) 0%, rgba(9,12,20,0.74) 72%, rgba(6,8,14,0.82) 100%)",
                  boxShadow:
                    "0 0 0 1px rgba(120,185,255,0.12) inset, 0 8px 24px rgba(0,0,0,0.42)",
                }}
              />

              <button
                type="button"
                aria-label="Dance"
                onPointerEnter={() => {
                  setEmoteHoverLabel(canDanceWhileCurrentState ? "dance" : "wave_only");
                }}
                onPointerLeave={() => {
                  setEmoteHoverLabel(null);
                }}
                onFocus={() => {
                  setEmoteHoverLabel(canDanceWhileCurrentState ? "dance" : "wave_only");
                }}
                onBlur={() => {
                  setEmoteHoverLabel(null);
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  setEmoteHoverLabel(null);
                  if (!canDanceWhileCurrentState) {
                    return;
                  }
                  onDanceEmote?.();
                }}
                disabled={!canDanceWhileCurrentState}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: 8,
                  transform: `translateX(-50%) scale(${danceHovered ? 1.08 : 1})`,
                  width: 46,
                  height: 46,
                  borderRadius: "50%",
                  border: danceHovered
                    ? "1px solid rgba(186,235,255,1)"
                    : "1px solid rgba(126,216,255,0.9)",
                  background:
                    "linear-gradient(180deg, rgba(33,72,114,0.95), rgba(18,40,70,0.98))",
                  color: "#e6f7ff",
                  display: "grid",
                  placeItems: "center",
                  pointerEvents: "auto",
                  cursor: canDanceWhileCurrentState ? "pointer" : "not-allowed",
                  opacity: canDanceWhileCurrentState ? 1 : 0.45,
                  transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                  boxShadow:
                    danceHovered
                      ? "0 8px 20px rgba(0,0,0,0.4), 0 0 0 1px rgba(186,235,255,0.42) inset"
                      : "0 6px 18px rgba(0,0,0,0.35), 0 0 0 1px rgba(126,216,255,0.2) inset",
                }}
              >
                <IconMusic size={20} stroke={1.9} />
              </button>

              <button
                type="button"
                aria-label="Wave"
                onPointerEnter={() => {
                  setEmoteHoverLabel("wave");
                }}
                onPointerLeave={() => {
                  setEmoteHoverLabel(null);
                }}
                onFocus={() => {
                  setEmoteHoverLabel("wave");
                }}
                onBlur={() => {
                  setEmoteHoverLabel(null);
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  setEmoteHoverLabel(null);
                  onWaveEmote?.();
                }}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: `translateY(-50%) scale(${waveHovered ? 1.08 : 1})`,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: waveHovered
                    ? "1px solid rgba(186,235,255,1)"
                    : "1px solid rgba(126,216,255,0.86)",
                  background:
                    "linear-gradient(180deg, rgba(31,68,112,0.95), rgba(14,32,58,0.98))",
                  color: "#e6f7ff",
                  display: "grid",
                  placeItems: "center",
                  pointerEvents: "auto",
                  cursor: "pointer",
                  transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                  boxShadow:
                    waveHovered
                      ? "0 7px 18px rgba(0,0,0,0.4), 0 0 0 1px rgba(186,235,255,0.42) inset"
                      : "0 5px 14px rgba(0,0,0,0.35), 0 0 0 1px rgba(126,216,255,0.2) inset",
                }}
              >
                <IconHandMove size={18} stroke={1.9} />
              </button>

              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  bottom: 16,
                  transform: "translateX(-50%)",
                  fontSize: 11,
                  letterSpacing: "0.02em",
                  color: "rgba(231,240,255,0.84)",
                  background: "rgba(6,10,18,0.55)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 10,
                  padding: "2px 8px",
                  width: 118,
                  textAlign: "center",
                  pointerEvents: "none",
                  textTransform: emoteHoverLabel ? "lowercase" : "uppercase",
                  fontWeight: 700,
                }}
              >
                {emoteHoverLabel ?? "Emotes"}
              </div>
            </div>
          </Html>
        )}

        {showChatBubbles &&
          stackedBubbles.map((entry) => (
            <Billboard key={entry.key} position={[0, entry.centerY, 0]}>
              <mesh>
                <planeGeometry args={[entry.bubble.planeW, entry.bubble.planeH]} />
                <meshBasicMaterial
                  ref={(mat) => {
                    bubbleMatRefs.current[entry.stackIndex] = mat;
                  }}
                  map={entry.bubble.texture}
                  transparent
                  depthWrite={false}
                />
              </mesh>
            </Billboard>
          ))}
      </group>
    </>
  );
}

function areDrobbitAvatarPropsEqual(previous: Props, next: Props): boolean {
  return (
    previous.avatar === next.avatar &&
    previous.isSelf === next.isSelf &&
    previous.isLaying === next.isLaying &&
    previous.sampleTimeMs === next.sampleTimeMs &&
    previous.motionFeedRef === next.motionFeedRef &&
    previous.onSelect === next.onSelect &&
    previous.showChatBubbles === next.showChatBubbles &&
    previous.showEmoteWheel === next.showEmoteWheel &&
    previous.onToggleEmoteWheel === next.onToggleEmoteWheel &&
    previous.onDanceEmote === next.onDanceEmote &&
    previous.onWaveEmote === next.onWaveEmote
  );
}

export const DrobbitAvatar = memo(
  DrobbitAvatarInner,
  areDrobbitAvatarPropsEqual
);

useGLTF.preload(DROBBIT_GLB, true);
